class ResponsibilityAccordion extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.items = Array.from(this.querySelectorAll('[data-ra-item]'));
    this.images = Array.from(this.querySelectorAll('[data-ra-image]'));
    this.allowMultiple = this.dataset.allowMultiple === 'true';
    this.clickRipple = this.dataset.clickRipple === 'true';
    this.toggleDuration = Number.parseInt(this.dataset.toggleDuration, 10) || 450;
    this.toggling = false;
    this.effectTimers = new WeakMap();

    this.onToggle = this.handleToggle.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onPointerDown = this.handlePointerDown.bind(this);
    this.onBlockSelect = this.handleBlockSelect.bind(this);

    this.items.forEach((item) => {
      item.dataset.state = item.open ? 'open' : 'closed';
      item.addEventListener('toggle', this.onToggle);
      item.addEventListener('keydown', this.onKeydown);
      item.querySelector('summary')?.addEventListener('pointerdown', this.onPointerDown);
    });
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.handleIntersection, { rootMargin: '100px', threshold: 0.08 });
      this.observer.observe(this);
    } else {
      this.dataset.inView = 'true';
    }

    const openItem = this.items.find((item) => item.open);
    this.showImage(openItem?.dataset.raItem ?? 'default', false);
  }

  disconnectedCallback() {
    this.items?.forEach((item) => {
      item.removeEventListener('toggle', this.onToggle);
      item.removeEventListener('keydown', this.onKeydown);
      item.querySelector('summary')?.removeEventListener('pointerdown', this.onPointerDown);
      this.effectTimers?.get(item)?.forEach((timer) => window.clearTimeout(timer));
    });
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.observer?.disconnect();
  }

  handleToggle(event) {
    const item = event.currentTarget;
    if (this.toggling) return;

    item.dataset.state = item.open ? 'open' : 'closed';
    this.restartEffect(item, 'is-toggling', this.toggleDuration);

    if (item.open) {
      if (!this.allowMultiple) {
        this.toggling = true;
        this.items.forEach((other) => {
          if (other !== item) other.open = false;
        });
        this.toggling = false;
      }
      this.showImage(item.dataset.raItem, true);
    } else {
      const remainingOpenItem = this.items.find((other) => other.open);
      this.showImage(remainingOpenItem?.dataset.raItem ?? 'default', true);
    }
  }

  handleKeydown(event) {
    if (event.key !== 'Escape' || !event.currentTarget.open) return;
    event.preventDefault();
    event.currentTarget.open = false;
    event.currentTarget.querySelector('summary')?.focus();
  }

  handlePointerDown(event) {
    const summary = event.currentTarget;
    const item = summary.closest('[data-ra-item]');
    if (!item || !this.clickRipple) return;

    const bounds = summary.getBoundingClientRect();
    summary.style.setProperty('--click-x', `${event.clientX - bounds.left}px`);
    summary.style.setProperty('--click-y', `${event.clientY - bounds.top}px`);
    this.restartEffect(item, 'is-clicked', 640);
  }

  restartEffect(item, className, duration) {
    const itemTimers = this.effectTimers.get(item) || new Map();
    window.clearTimeout(itemTimers.get(className));
    item.classList.remove(className);

    requestAnimationFrame(() => {
      item.classList.add(className);
      const timer = window.setTimeout(() => item.classList.remove(className), duration);
      itemTimers.set(className, timer);
      this.effectTimers.set(item, itemTimers);
    });
  }

  handleBlockSelect(event) {
    const item = event.target.closest?.('[data-ra-item]');
    if (!item || !this.contains(item)) return;
    item.open = true;
    this.showImage(item.dataset.raItem, true);
  }

  showImage(key, animate = true) {
    const selected = this.images.find((image) => image.dataset.raImage === String(key))
      || this.images.find((image) => image.dataset.raImage === 'default');
    if (!selected) return;

    this.images.forEach((image) => {
      const active = image === selected;
      image.hidden = !active;
      image.setAttribute('aria-hidden', String(!active));
      image.classList.remove('is-entering');
    });

    if (animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      requestAnimationFrame(() => selected.classList.add('is-entering'));
    }
  }

  handleIntersection = (entries) => {
    if (entries[0]?.isIntersecting) {
      this.dataset.inView = 'true';
      this.observer?.disconnect();
    }
  };
}

if (!customElements.get('responsibility-accordion')) {
  customElements.define('responsibility-accordion', ResponsibilityAccordion);
}
